import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import axios from 'axios';
import '@/App.css';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import VendorManagement from './pages/VendorManagement';
import PurchaseRequest from './pages/PurchaseRequest';
import ApprovalDashboard from './pages/ApprovalDashboard';
import FinanceDashboard from './pages/FinanceDashboard';
import PaymentQueue from './pages/PaymentQueue';
import AdminDashboard from './pages/AdminDashboard';
import AccountsDashboard from './pages/AccountsDashboard';
import EntityManagement from './pages/EntityManagement';
import UserManagement from './pages/UserManagement';
import Layout from './components/Layout';
import { Toaster, toast } from './components/ui/sonner';

// Construct API URL, handling trailing slashes properly
const getApiUrl = () => {
  let baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
  // Remove trailing slash if present
  baseUrl = baseUrl.replace(/\/$/, '');
  // Add /api if not already present
  return baseUrl.endsWith('/api') ? baseUrl : `${baseUrl}/api`;
};

export const API = getApiUrl();

export const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Clear session and redirect to login (e.g. when token expired / 401).
 * Use window.location so the app fully reloads and state is cleared.
 */
export const clearSessionAndRedirectToLogin = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  window.location.href = '/login';
};

export const getUser = () => {
  const user = localStorage.getItem('user');
  return user ? JSON.parse(user) : null;
};

const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem('token');
  const user = getUser();
  
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login';
  };
  
  return (
    <Layout user={user} onLogout={handleLogout}>
      {children}
    </Layout>
  );
};

function App() {
  const [user, setUser] = useState(null);

  // Global handler: on 401 (session expired), clear session and redirect to login
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error.response?.status;
        const isLoginRequest = error.config?.url?.includes?.('/auth/login');
        if (status === 401 && !isLoginRequest) {
          toast.error('Session expired. Please log in again.');
          clearSessionAndRedirectToLogin();
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  useEffect(() => {
    const storedUser = getUser();
    if (storedUser) {
      setUser(storedUser);
    }
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login setUser={setUser} />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Dashboard user={user} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/vendors"
            element={
              <ProtectedRoute>
                <VendorManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/purchase-requests"
            element={
              <ProtectedRoute>
                <PurchaseRequest />
              </ProtectedRoute>
            }
          />
          <Route
            path="/approvals"
            element={
              <ProtectedRoute>
                <ApprovalDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/finance"
            element={
              <ProtectedRoute>
                <FinanceDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payments"
            element={
              <ProtectedRoute>
                <PaymentQueue />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/entities"
            element={
              <ProtectedRoute>
                <EntityManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/user-management"
            element={
              <ProtectedRoute>
                <UserManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/accounts"
            element={
              <ProtectedRoute>
                <AccountsDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/accounts/purchase-requests"
            element={
              <ProtectedRoute>
                <AccountsDashboard section="purchase_requests" />
              </ProtectedRoute>
            }
          />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
